'use client';

import Image from 'next/image';
import Link from 'next/link';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface StoneInvestmentFooterTeaserProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonHref?: string;
  backgroundImage?: string;
  locale?: string;
  messages?: Record<string, unknown>;
}

/**
 * Stone Investment - Footer Teaser Section
 * 
 * Concept One Design (Exact Match):
 * - Full-width section with 80vh height
 * - Dark overlay with image that zooms on hover
 * - Centered text with italic heading
 * - White CTA button with shadow
 */
export function StoneInvestmentFooterTeaser({
  title: titleProp,
  subtitle: subtitleProp,
  buttonText: buttonTextProp,
  buttonHref,
  backgroundImage = 'https://res.cloudinary.com/elementary-media/image/upload/v1769766718/Generated_Image_January_30_2026_-_1_49PM_vdtcoc.jpg',
  locale = 'en',
  messages,
}: StoneInvestmentFooterTeaserProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const teaserMessages = (stoneMessages.footerTeaser || {}) as Record<string, string>;
  const accessMessages = (stoneMessages.accessibility || {}) as Record<string, string>;

  const title = teaserMessages.title || titleProp || 'Selling Distinction';
  const subtitle = teaserMessages.subtitle || subtitleProp || 'Let us tell the story of your property';
  const buttonText = teaserMessages.buttonText || buttonTextProp || 'Request a Private Valuation';
  const rawHref = buttonHref || `/contact?intent=valuation`;
  const href =
    rawHref.startsWith('/') && !rawHref.startsWith(`/${locale}`)
      ? `/${locale}${rawHref}`
      : rawHref;

  return (
    <section className="relative h-[80vh] flex items-center justify-center bg-stone-900 text-stone-50 overflow-hidden group cursor-pointer">
      {/* Background Image with Zoom Effect */}
      <div className="absolute inset-0 opacity-40 transition-opacity duration-700 group-hover:opacity-30">
        <Image 
          src={backgroundImage}
          alt={accessMessages.footerBackgroundAlt || 'Footer Background'}
          fill
          className="object-cover scale-100 group-hover:scale-105 transition-transform duration-[2000ms]"
          loading="lazy"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center space-y-8 p-6">
        <h2 className="font-serif text-5xl md:text-8xl italic">{title}</h2>
        <p className="font-sans uppercase tracking-[0.2em] text-sm text-stone-300">
          {subtitle}
        </p>
        <div className="pt-8">
          <Link 
            href={href}
            onClick={() => analyticsEvents.custom('cta_click', { cta_text: buttonText, cta_location: 'footer_teaser', cta_destination: href, cta_variant: 'primary' })}
            className="px-12 py-5 bg-white text-stone-900 hover:bg-stone-200 transition-all duration-500 uppercase tracking-widest text-xs shadow-2xl inline-block font-sans"
          >
            {buttonText}
          </Link>
        </div>
      </div>
    </section>
  );
}
